/* Local candidate. No cart listeners; the existing product-form owns submission. */
(() => {
  'use strict';
  const ENABLED = false;
  const PRODUCTS = Object.freeze({
    '10380777357529': 1, '10380778701017': 1, '10380779028697': 1, '10380779454681': 1,
    '10380909183193': 4, '10380909510873': 4, '10380909773017': 4, '10380910133465': 4,
  });
  const FEE_IDS = Object.freeze({text: '51323299135705', logo: '51323299168473', setup: '51323299234009'});
  const RATES = Object.freeze({text: 699, logo: 999, setup: 1488});
  const GUEST_AUTH_KEY = 'wcf-personalization:d3bd44-b6.myshopify.com:guest-auth:v1';
  const POSITIONS = Object.freeze({'left-chest': 'Left chest', 'right-chest': 'Right chest', 'left-sleeve': 'Left sleeve', 'right-sleeve': 'Right sleeve'});
  const positive = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const assert = (condition, message) => { if (!condition) throw new Error(message); };

  function productFormLine(formData) {
    const id = formData.get('id'), quantity = formData.get('quantity') || '1';
    assert(positive(id) && positive(quantity), 'Choose a valid product and quantity.');
    const properties = {};
    const seenProperties = new Set();
    for (const [name, value] of formData.entries()) {
      if (!name.startsWith('properties[')) continue;
      const match = /^properties\[([^\]]+)\]$/.exec(name);
      assert(match && typeof value === 'string', 'A product option could not be preserved.');
      const key = match[1];
      assert(!seenProperties.has(key), 'Duplicate product options require review.');
      seenProperties.add(key);
      // EXPO pricing metadata is never stripped to suppress a charge. New option
      // sets must be Measurements-only and must produce no pricing metadata.
      assert(!value || (!/^_(?:expo_|wcf_customization_|pc_)/.test(key)
        && !['Embroidery', 'Embroidery Text', 'EmbroideryText', 'Embroidery Color', 'Custom Logo', 'Logo', 'Upload Your Logo'].includes(key)),
      'Existing personalization options must be cleared before using this selection.');
      // Empty optional fields are omitted before Shopify cart submission.
      if (value !== '') properties[key] = value;
    }
    const result = {id: String(id), quantity: Number(quantity), properties};
    if (formData.get('selling_plan')) result.selling_plan = String(formData.get('selling_plan'));
    return result;
  }

  function selectionLine(base, selection, assetId = null) {
    assert(selection && (selection.textOn || selection.logoOn), 'Choose personalization first.');
    assert(!selection.textOn || (selection.text?.trim() && selection.color && POSITIONS[selection.textPlacement]), 'Complete the text and thread color.');
    assert(!selection.logoOn || (assetId && ['embroidery', 'heat'].includes(selection.method) && POSITIONS[selection.logoPlacement]), 'Complete the logo selection.');
    return {
      variantId: base.id, quantity: base.quantity,
      text: selection.textOn ? selection.text.trim() : '',
      textColor: selection.textOn ? selection.color : null,
      textPlacement: selection.textOn ? selection.textPlacement : null,
      assetId: selection.logoOn ? assetId : null,
      method: selection.logoOn ? selection.method : 'none',
      logoPlacement: selection.logoOn ? selection.logoPlacement : null,
    };
  }

  const DETAIL_LABELS = ["Height (feet & inches)", "Weight (pounds)", "Bust (inches)", "Waist (inches)", "Hip Circumference (inches)", "Upper Arm Circumference (inches)", "Inseam (inches)", "Outseam (inches)", "Shoulder Width (inches)", "Crotch Length (inches)", "Sizes of set worn in the past", "Scrub Set preferences"];
  const PERSONAL_DETAIL_LABELS = ['Personalization text','Thread color','Text placement','Logo method','Logo placement'];
  const detailKey = label => `_wcf_detail_${label}`;
  function normalizeDetails(properties) {
    const result = {...properties};
    const individual = DETAIL_LABELS.filter(label => result[label] !== undefined || result[detailKey(label)] !== undefined);
    if (result.Measurements !== undefined && individual.length) {
      const expected = individual.map(label => `${label}: ${result[label] ?? result[detailKey(label)]}`).join('\n');
      assert(typeof result.Measurements === 'string' && result.Measurements.replace(/\r\n|\r/g,'\n') === expected.replace(/\r\n|\r/g,'\n'), 'Measurements conflict and need review.');
      delete result.Measurements;
    }
    for (const label of [...DETAIL_LABELS, ...PERSONAL_DETAIL_LABELS, 'Measurements']) {
      const key = detailKey(label);
      if (Object.hasOwn(result,label)) {
        assert(!Object.hasOwn(result,key) || result[key] === result[label], 'Conflicting details need review.');
        result[key] = result[label]; delete result[label];
      }
    }
    return result;
  }
  function quoteToCartPayload(quote, bases, requested, options = {}) {
    const now = options.now ?? Date.now();
    assert(quote && typeof quote.quoteId === 'string' && quote.quoteId.length > 0 && Number.isFinite(quote.expiresAt) && quote.expiresAt > now,
      'The personalization quote has expired. Please try again.');
    assert(Array.isArray(quote.lines) && Array.isArray(quote.fees) && quote.lines.length === bases.length && bases.length === requested.length,
      'The personalization quote does not match your selection.');
    const references = new Set();
    const items = quote.lines.map((line, index) => {
      const base = bases[index], wanted = requested[index];
      assert(String(line.variantId) === base.id && line.quantity === base.quantity && wanted.variantId === base.id && wanted.quantity === base.quantity,
        'The product changed while preparing your quote.');
      assert(typeof line.lineRef === 'string' && line.lineRef && !references.has(line.lineRef)
        && line.properties?._pc_quote === quote.quoteId && line.properties?._pc_line === line.lineRef,
      'Invalid personalization quote reference.');
      assert(Object.keys(line.properties).every(key => key.startsWith('_pc_')),
        'The quote would replace an existing product option.');
      for (const key of ['text', 'textColor', 'textPlacement', 'assetId', 'method', 'logoPlacement']) {
        if (Object.hasOwn(line, key)) assert(line[key] === wanted[key], 'The quote does not match the saved personalization.');
      }
      references.add(line.lineRef);
      const details = {};
      if (wanted.text) {
        details[detailKey('Personalization text')] = wanted.text;
        details[detailKey('Thread color')] = wanted.textColor;
        details[detailKey('Text placement')] = POSITIONS[wanted.textPlacement];
      }
      if (wanted.method !== 'none') {
        details[detailKey('Logo method')] = wanted.method === 'heat' ? 'Heat transfer' : 'Embroidery';
        details[detailKey('Logo placement')] = POSITIONS[wanted.logoPlacement];
      }
      const summary = [wanted.text ? 'Text embroidery' : '', wanted.method === 'heat' ? 'Heat-transfer logo' : wanted.method === 'embroidery' ? 'Embroidered logo' : ''].filter(Boolean).join(' + ');
      const normalized = normalizeDetails(base.properties);
      for (const [key,value] of Object.entries(details)) assert(!Object.hasOwn(normalized,key) || normalized[key] === value, 'Personalization details conflict and need review.');
      // Display-only association: fee eligibility and amounts still come from the server quote.
      const displayGroups = (Array.isArray(quote.groups) ? quote.groups : []).filter(group =>
        line.method === 'heat' && line.assetHash && group.hash === line.assetHash &&
        group.method === line.method && group.spec === line.spec && typeof group.groupRef === 'string' && group.groupRef);
      const displayProperties = displayGroups.length === 1 ? {_pc_display_group: displayGroups[0].groupRef} : {};
      return {...base, properties: {...normalized, ...details, Customization:summary, ...line.properties, ...displayProperties}};
    });
    const fees = new Set();
    for (const fee of quote.fees) {
      const p = fee.properties;
      assert(FEE_IDS[fee.component] === String(fee.variantId) && positive(fee.quantity) && RATES[fee.component] === fee.unitCents
        && p?._pc_quote === quote.quoteId && p?._pc_component === fee.component,
      'The personalization fee could not be verified.');
      const group = fee.component === 'setup' ? p._pc_group : p._pc_line;
      assert(typeof group === 'string' && group && (fee.component === 'setup' || references.has(group)), 'The personalization fee has no matching product.');
      if (fee.component === 'setup') assert(Number(fee.quantity) === 1, 'Invalid setup fee quantity.');
      const key = `${fee.component}:${group}`;
      assert(!fees.has(key), 'Duplicate personalization fee.');
      fees.add(key);
      items.push({id: String(fee.variantId), quantity: Number(fee.quantity), properties: {...p}});
    }
    quote.lines.forEach((line, index) => {
      assert(fees.has(`text:${line.lineRef}`) === Boolean(requested[index].text)
        && fees.has(`logo:${line.lineRef}`) === (requested[index].method !== 'none'),
      'A personalization service fee is missing or unexpected.');
    });
    const payload = {items};
    if (options.sections) payload.sections = options.sections;
    if (options.sectionsUrl) payload.sections_url = options.sectionsUrl;
    return payload;
  }

  // HTML form submission canonicalizes line endings. Do not normalize other fields.
  const equalCartProperty = (key, actual, expected) => key === 'Measurements' && typeof actual === 'string' && typeof expected === 'string'
    ? actual.replace(/\r\n|\r/g, '\n') === expected.replace(/\r\n|\r/g, '\n')
    : actual === expected;

  function verifyCartReadback(cart, prepared) {
    const parentKeys = new Map();
    for (const line of prepared.quote.lines) {
      const matches = cart.items.filter(item => item.properties?._pc_quote === prepared.quote.quoteId && !item.properties?._pc_component && item.properties?._pc_line === line.lineRef);
      assert(matches.length === 1 && matches[0].key, "Parent identity could not be verified.");
      parentKeys.set(line.lineRef, matches[0].key);
    }
    assert(cart?.currency === 'USD', 'A USD cart is required.');
    const items = cart?.items?.filter(item => item.properties?._pc_quote === prepared.quote.quoteId);
    assert(Array.isArray(items) && items.length === prepared.payload.items.length,
      'Cart verification failed. Review your cart before trying again.');
    for (const wanted of prepared.payload.items) {
      const matches = items.filter(item => String(item.variant_id ?? item.id) === wanted.id
        && Object.entries(wanted.properties).every(([key, value]) => equalCartProperty(key, item.properties?.[key], value)));
      assert(matches.length === 1 && matches[0].quantity === wanted.quantity,
        'Cart verification failed. Review your cart before trying again.');
      const component = wanted.properties._pc_component;
      if (component) {
        assert(matches[0].final_price === RATES[component], 'A personalization fee changed. Review your cart before checkout.');
        const owner = feeOwner(prepared.quote, wanted);
        assert(matches[0].parent_relationship?.parent_key === parentKeys.get(owner), 'Service parent relationship could not be verified.');
      }
    }
    return true;
  }

  function feeOwner(quote, fee) {
    const p = fee.properties;
    if (p._pc_component !== 'setup') return p._pc_line;
    const groups = (quote.groups || []).filter(g => g.groupRef === p._pc_group);
    assert(groups.length === 1, 'Setup group could not be verified.');
    const group = groups[0];
    const lines = quote.lines.filter(line => line.method === group.method && line.assetHash === group.hash && line.spec === group.spec);
    assert(lines.length > 0, 'Setup has no matching product.');
    return lines.map(line => line.lineRef).sort()[0];
  }

  const sortedProperties = properties => Object.fromEntries(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  const itemState = item => ({key: item.key, id: String(item.variant_id ?? item.id), quantity: item.quantity,
    properties: sortedProperties(item.properties), finalPrice: item.final_price,
    sellingPlan: item.selling_plan_allocation?.selling_plan?.id || null, parentKey: item.parent_relationship?.parent_key || null});
  function cartState(cart) {
    assert(Array.isArray(cart?.items), 'The cart could not be checked.');
    return JSON.stringify({currency: cart.items.length ? cart.currency : null, note: cart.note == null ? '' : cart.note, attributes: sortedProperties(cart.attributes),
      items: cart.items.map(itemState).sort((a, b) => String(a.key).localeCompare(String(b.key)))});
  }
  async function cartHash(cart) {
    const bytes = new TextEncoder().encode(cartState(cart));
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), value => value.toString(16).padStart(2, '0')).join('');
  }
  function reconciliationInputs(cart, storedQuotes) {
    const bases = [], requested = [], oldKeys = [], parentRefs = new Set();
    for (const item of cart.items) {
      const properties = item.properties || {};
      if (!Object.keys(properties).some(key => key.startsWith('_pc_'))) continue;
      const quote = storedQuotes[properties._pc_quote];
      assert(quote?.quoteId === properties._pc_quote && typeof item.key === 'string', 'A saved personalization quote is unavailable.');
      oldKeys.push(item.key);
      if (properties._pc_component) {
        assert(FEE_IDS[properties._pc_component] === String(item.variant_id ?? item.id), 'An unknown personalization service needs review.');
        continue;
      }
      assert(Object.hasOwn(PRODUCTS, String(item.product_id)), 'This product is outside the personalization preview.');
      const line = quote.lines?.find(line => line.lineRef === properties._pc_line);
      const ref = `${properties._pc_quote}:${properties._pc_line}`;
      assert(line && String(line.variantId) === String(item.variant_id ?? item.id) && !parentRefs.has(ref) && positive(item.quantity),
        'A personalized product no longer matches its saved quote.');
      parentRefs.add(ref);
      const clean = Object.fromEntries(Object.entries(properties).filter(([key]) => !key.startsWith('_pc_')));
      const base = {id: String(item.variant_id ?? item.id), quantity: item.quantity, properties: clean};
      if (item.selling_plan_allocation?.selling_plan?.id) base.selling_plan = String(item.selling_plan_allocation.selling_plan.id);
      bases.push(base);
      requested.push({variantId: base.id, quantity: base.quantity, text: line.text, textColor: line.textColor,
        textPlacement: line.textPlacement, assetId: line.assetId, method: line.method, logoPlacement: line.logoPlacement});
    }
    assert(bases.length > 0, 'No personalized product is available to review.');
    return {bases, requested, oldKeys};
  }

/** Pure planning: all associations are checked against the authenticated server quote. */
function pcQuantityPlan(cart,key,quantity,quote,products,feeIds){
 const check=(x,m)=>{if(!x)throw Error(m)};
 check(Array.isArray(cart?.items)&&Number.isSafeInteger(quantity)&&quantity>=0&&quantity<=100,'Invalid cart or quantity');
 const parent=cart.items.find(x=>x.key===key),p=parent?.properties;check(parent&&p?._pc_quote&&!p._pc_component&&p._pc_line,'Selected line is not a personalized product');
 check(quote?.quoteId===p._pc_quote&&Array.isArray(quote.lines)&&Array.isArray(quote.fees)&&Array.isArray(quote.groups),'Quote unavailable');
 const scoped=cart.items.filter(x=>x.properties?._pc_quote===quote.quoteId),parents=scoped.filter(x=>!x.properties?._pc_component),fees=scoped.filter(x=>x.properties?._pc_component);
 const refs=new Map(),groups=new Map();
 for(const line of quote.lines){check(line.lineRef&&line.properties?._pc_quote===quote.quoteId&&line.properties?._pc_line===line.lineRef&&!refs.has(line.lineRef),'Invalid quote lines');refs.set(line.lineRef,line);}
 for(const group of quote.groups){check(group.groupRef&&group.hash&&group.method==='heat'&&typeof group.spec==='string'&&typeof group.setupReusable==='boolean'&&!groups.has(group.groupRef),'Invalid setup group');groups.set(group.groupRef,group);}
 for(const line of quote.lines.filter(x=>x.method==='heat'))check([...groups.values()].filter(g=>g.hash===line.assetHash&&g.spec===line.spec).length===1,'Ambiguous heat setup group');
 const active=new Map();for(const item of parents){const props=item.properties,line=refs.get(props._pc_line),sets=products[String(item.product_id)];check(item.key&&line&&String(item.variant_id??item.id)===String(line.variantId)&&[1,4].includes(sets)&&Number.isSafeInteger(item.quantity)&&item.quantity>0&&!active.has(props._pc_line),'Parent mismatch');active.set(props._pc_line,{item,line,sets});}
 check(active.has(p._pc_line)&&active.get(p._pc_line).item.key===key,'Selected parent mismatch');
 const canonical=new Map();for(const f of quote.fees){const c=f.component,ref=c==='setup'?f.properties?._pc_group:f.properties?._pc_line;const id=`${c}:${ref}`;check(['text','logo','setup'].includes(c)&&String(f.variantId)===String(feeIds[c])&&f.unitCents===RATES[c]&&f.properties?._pc_quote===quote.quoteId&&f.properties?._pc_component===c&&ref&&!canonical.has(id),'Invalid quoted fee');if(c==='setup')check(groups.has(ref)&&groups.get(ref).setupReusable===false&&f.quantity===1,'Invalid quoted setup');else check(refs.has(ref),'Unknown fee parent');canonical.set(id,f);}
 const groupActive=(group,after)=>[...active.values()].some(({item,line})=>(after&&item.key===key?quantity:item.quantity)>0&&line.method==='heat'&&line.assetHash===group.hash&&line.spec===group.spec);
 const flat=fees.length>0&&fees.every(f=>!f.parent_relationship);
 check(flat||fees.every(f=>f.parent_relationship?.parent_key),'Mixed or invalid service relationships');
 const seen=new Set(),updates={[key]:quantity};
 for(const fee of fees){const prop=fee.properties,c=prop._pc_component,ref=c==='setup'?prop._pc_group:prop._pc_line,id=`${c}:${ref}`;check(fee.key&&canonical.has(id)&&!seen.has(id)&&String(fee.variant_id??fee.id)===String(feeIds[c])&&fee.final_price===RATES[c],'Service line mismatch');seen.add(id);
  const owner=flat?null:parents.find(item=>item.key===fee.parent_relationship?.parent_key);check(flat||owner,'Service parent relationship missing');
  if(c==='setup'){const group=groups.get(ref), ownerLine=owner&&refs.get(owner.properties._pc_line);check(flat||(ownerLine?.method===group.method&&ownerLine.assetHash===group.hash&&ownerLine.spec===group.spec),'Setup owner mismatch');check(groupActive(group,false)&&fee.quantity===1,'Setup association mismatch');if(groupActive(group,true)===false)updates[fee.key]=0;}
  else {const row=active.get(ref);check(row&&(flat||owner.key===row.item.key)&&Number.isSafeInteger(fee.quantity)&&fee.quantity===row.item.quantity*row.sets,'Service quantity mismatch');if(row.item.key===key)updates[fee.key]=quantity*row.sets;}
 }
 for(const [id,f] of canonical){const c=f.component,ref=c==='setup'?f.properties._pc_group:f.properties._pc_line;const expected=c==='setup'?groupActive(groups.get(ref),false):active.has(ref);check(!expected||seen.has(id),'Required service line missing');}
 return updates;
}

  function createAdapter(config = {}, fetcher, origin, suppliedStorage) {
    const enabled = config.enabled === true;
    const selections = new Map(), uploads = new WeakMap();
    // Only server-issued opaque guest authority may enter sessionStorage. Prices,
    // quote data and setup eligibility never do. Blocked storage falls back to RAM.
    let guestToken = null, needsReview = false, reconciling = false, authorizedQueue = Promise.resolve();
    const lockToken = Symbol();
    let sessionStore = null, sessionStorageAvailable = false;
    if (enabled) {
      try {
        sessionStore = suppliedStorage === undefined && typeof window !== 'undefined' ? window.sessionStorage : suppliedStorage;
        const stored = sessionStore?.getItem(GUEST_AUTH_KEY);
        if (stored && /^[a-f0-9]{64}$/.test(stored)) guestToken = stored;
        else if (stored) sessionStore.removeItem(GUEST_AUTH_KEY);
        sessionStorageAvailable = Boolean(sessionStore);
      } catch (_) { sessionStore = null; }
    }
    function saveGuestAuthority(token) {
      assert(/^[a-f0-9]{64}$/.test(token), 'Invalid upload session.');
      guestToken = token;
      try { if (sessionStore) { sessionStore.setItem(GUEST_AUTH_KEY, token); sessionStorageAvailable = true; } }
      catch (_) { sessionStorageAvailable = false; }
    }
    const proxy = '/apps/wcf-personalization';
    const productId = product => String(product?.dataset?.productId || '');
    const scoped = product => enabled && Object.hasOwn(PRODUCTS, productId(product));
    const configuredRoot = config.routesRoot || '/';
    assert(typeof configuredRoot === 'string' && configuredRoot.startsWith('/') && !configuredRoot.startsWith('//') && !/[?#]/.test(configuredRoot), 'Invalid cart route.');
    const root = configuredRoot.endsWith('/') ? configuredRoot : `${configuredRoot}/`;
    const url = path => new URL(path, origin).href;
    async function request(path, options = {}, authorized = false) {
      const perform = async () => {
      const headers = {'Accept': 'application/json', ...options.headers};
      if (authorized && guestToken && /\/quote\//.test(path)) {
        const claim = await fetcher(url(`${proxy}/claim-guest`), {method:'POST',headers:{'Accept':'application/json','x-guest-token':guestToken},credentials:'same-origin',cache:'no-store'});
        if(claim.ok) { guestToken=null; try { if(sessionStore) sessionStore.removeItem(GUEST_AUTH_KEY); } catch (_) { sessionStorageAvailable=false; } }
        else if(claim.status!==401) throw new Error('Your guest session could not be imported. Please review your cart.');
      }
      if (authorized && guestToken) headers['x-guest-token'] = guestToken;
      const response = await fetcher(url(path), {...options, headers, credentials: 'same-origin', cache: 'no-store'});
      const body = await response.json();
      if (!response.ok && authorized && /\/quote\//.test(path) && !guestToken && response.status === 400) {
        throw new Error('Your guest session could not be restored. Sign in, or return to the product and choose your logo again.');
      }
      assert(response.ok && !body.status && !body.error, body.description || body.error || 'Personalization is unavailable. Please try again later.');
      if (authorized && body.guestToken) {
        saveGuestAuthority(body.guestToken);
      }
      const {guestToken: authority, ...safeBody} = body;
      return safeBody;
      };
      if (!authorized) return perform();
      const pending = authorizedQueue.then(perform, perform);
      authorizedQueue = pending.catch(() => undefined);
      return pending;
    }
    async function existingPersonalizedInputs(cart, locked = false) {
      assert(Array.isArray(cart.items) && cart.currency === 'USD', 'A USD cart is required.');
      assert(!cart.items.some(item => Object.keys(item.properties || {}).some(key => key.startsWith('_wcf_customization_'))), 'Existing legacy personalization needs review.');
      const ids = [...new Set(cart.items.map(item => item.properties?._pc_quote).filter(Boolean))];
      assert(!cart.items.some(item => Object.keys(item.properties || {}).some(key => key.startsWith('_pc_')) && !item.properties?._pc_quote), 'Unknown personalized item needs review.');
      if (!ids.length) return {bases:[], requested:[], oldKeys:[], ids:[]};
      assert(config.reconciliationEnabled === true && !needsReview && (!reconciling || locked), 'Cart reconciliation is not enabled.');
      const storedQuotes = Object.fromEntries(await Promise.all(ids.map(async id => {
        assert(/^[0-9a-f-]{36}$/.test(id), 'Invalid quote reference.');
        return [id, await request(`${proxy}/quote/${encodeURIComponent(id)}`, {}, true)];
      })));
      const inputs = reconciliationInputs(cart, storedQuotes);
      for (const id of ids) {
        const parent = cart.items.find(item => item.properties?._pc_quote === id && !item.properties?._pc_component);
        assert(parent, 'An orphaned service requires review.');
        pcQuantityPlan(cart, parent.key, parent.quantity, storedQuotes[id], PRODUCTS, FEE_IDS);
      }
      return {...inputs, ids};
    }
    async function upload(file) {
      assert(file && ['image/png', 'image/jpeg'].includes(file.type) && file.size > 0 && file.size <= 10 * 1024 * 1024, 'Choose a PNG or JPEG no larger than 10 MB.');
      if (uploads.has(file)) return uploads.get(file);
      const pending = (async () => {
        const uploaded = await request(`${proxy}/upload`, {method: 'POST', headers: {'Content-Type': file.type}, body: file}, true);
        assert(typeof uploaded.id === 'string' && uploaded.id, 'The logo upload could not be verified.');
        return uploaded.id;
      })();
      uploads.set(file, pending);
      try { return await pending; } catch (error) { uploads.delete(file); throw error; }
    }
    return Object.freeze({
      enabled,
      checkoutContinuationEnabled: enabled && config.reconciliationEnabled === true && config.checkoutContinuationEnabled === true,
      async readCurrentCart() { return request(`${root}cart.js`); },
      hashCart: cartHash,
      get needsReview() { return needsReview; },
      get guestSessionNotice() { return guestToken && !sessionStorageAvailable ? 'Your browser could not save this guest session. After a reload, sign in or choose your logo again.' : ''; },
      markCartUncertain() { needsReview = true; },
      fixedSetCount: product => scoped(product) ? PRODUCTS[productId(product)] : 0,
      setSelection(product, selection) { if (Object.hasOwn(PRODUCTS, productId(product))) selections.set(productId(product), {...selection}); },
      canHandle(product) { const s = selections.get(productId(product)); return scoped(product) && Boolean(s?.textOn || s?.logoOn); },
      // This only reads the account library. The proxy verifies customer identity.
      async library() { assert(enabled, 'Personalization checkout is not enabled.'); let importFailed=false; if (guestToken) { try { await request(`${proxy}/claim-guest`, {method:'POST'}, true); guestToken=null; try { if(sessionStore) sessionStore.removeItem(GUEST_AUTH_KEY); } catch (_) { sessionStorageAvailable=false; } } catch (_) { importFailed=true; } } const library=await request(`${proxy}/library`); if(importFailed && typeof document!=='undefined') for(const status of document.querySelectorAll('[data-library-status]')) { if(!status.parentElement.querySelector('[data-claim-warning]')) { const note=document.createElement('p'); note.dataset.claimWarning=''; note.setAttribute('role','status'); note.textContent='Your guest logos could not be imported. Your saved account logos are still available.'; status.after(note); } } return library; },
      async assetBlob(id) {
        assert(enabled && /^[0-9a-f-]{36}$/.test(id), 'This saved logo is unavailable.');
        const headers = {'Accept': 'image/png,image/jpeg'};
        if (guestToken) headers['x-guest-token'] = guestToken;
        const response = await fetcher(url(`${proxy}/asset/${encodeURIComponent(id)}`),
          {headers, credentials: 'same-origin', cache: 'no-store'});
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || 'The saved logo could not be loaded.');
        }
        assert(['image/png', 'image/jpeg'].includes(response.headers.get('content-type')), 'The saved logo format is unsupported.');
        const blob = await response.blob();
        assert(blob.size > 0 && blob.size <= 10 * 1024 * 1024, 'The saved logo size is unsupported.');
        return blob;
      },
      async quoteSelection(productInfo, state, quantity = 1) {
        assert(scoped(productInfo), 'Personalization quotes are unavailable in this preview.');
        const id = String(productInfo.querySelector('form[action*="/cart/add"] [name="id"]')?.value || '');
        assert(positive(id) && positive(quantity), 'Choose an available product variant.');
        const cart = await request(`${root}cart.js`), beforeHash = await cartHash(cart);
        const inputs = await existingPersonalizedInputs(cart);
        const assetId = state.logoOn ? (state.assetId || await upload(state.file)) : null;
        const base = {id, quantity: Number(quantity), properties: {}}, wanted = selectionLine(base, state, assetId);
        const bases = [...inputs.bases, base], requested = [...inputs.requested, wanted];
        const quote = await request(`${proxy}/cart-quote`, {method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({lines: requested, spec: 'Standard chest'})}, true);
        quoteToCartPayload(quote, bases, requested);
        const freshPreviewCart = await request(`${root}cart.js`);
        assert(freshPreviewCart.currency === 'USD', 'A USD cart is required.');
        assert(await cartHash(freshPreviewCart) === beforeHash, 'Your cart changed. Please update your quote.');
        const ordinary = cart.items.filter(item => !inputs.oldKeys.includes(item.key));
        const ordinaryTotal = ordinary.reduce((sum,item) => {
          assert(Number.isSafeInteger(item.final_line_price) && item.final_line_price >= 0, 'Cart total is unavailable.');
          return sum + item.final_line_price;
        }, 0);
        assert(Number.isSafeInteger(quote.totalCents) && Number.isSafeInteger(ordinaryTotal + quote.totalCents), 'Cart total is unavailable.');
        return {...quote, preview: {newLineRef:quote.lines.at(-1).lineRef, existingCart:cart.items.length > 0, cartTotalCents:ordinaryTotal + quote.totalCents}};
      },
      async prepareCartAdd({productInfo, formData}) {
        assert(this.canHandle(productInfo) && !needsReview, 'Personalization checkout is unavailable until your cart has been reviewed.');
        const base = productFormLine(formData), selection = selections.get(productId(productInfo));
        const cart = await request(`${root}cart.js`);
        assert(Array.isArray(cart.items), 'The cart could not be checked.');
        const inputs = await existingPersonalizedInputs(cart);
        const ids = inputs.ids;
        const assetId = selection.logoOn ? (selection.assetId || await upload(selection.file)) : null;
        const wanted = selectionLine(base, selection, assetId), bases = [...inputs.bases, base], requested = [...inputs.requested, wanted];
        const quote = await request(`${proxy}/cart-quote`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({lines: requested, spec: 'Standard chest'})}, true);
        const payload = quoteToCartPayload(quote, bases, requested, {sections: String(formData.get('sections') || '').split(',').filter(Boolean), sectionsUrl: formData.get('sections_url') || undefined});
        return {quote, payload, before:cart, beforeHash:await cartHash(cart), oldKeys:inputs.oldKeys, productAdd:true, newLineRef:quote.lines.at(-1).lineRef};
      },
      async guardedInitialAdd(prepared, send) {
        assert(enabled && !needsReview && !reconciling && prepared && !prepared.productAdd && typeof send === 'function', 'Personalization checkout is unavailable.');
        const current = await request(`${root}cart.js`);
        assert(current.currency === 'USD', 'A USD cart is required.');
        assert(await cartHash(current) === prepared.beforeHash, 'Your cart changed. Review it before trying again.');
        try { return await send(); } catch (error) { needsReview = true; error.needsReview = true; throw error; }
      },
      async confirmCart(prepared) {
        try {
          const current = await request(`${root}cart.js`);
          verifyCartReadback(current, prepared);
          const expected = prepared.before.items.filter(item => !prepared.oldKeys.includes(item.key));
          const actual = current.items.filter(item => item.properties?._pc_quote !== prepared.quote.quoteId);
          assert(cartState({...current, items:actual}) === cartState({...prepared.before, items:expected}), 'Your cart changed. Review it before checkout.');
          return true;
        } catch (error) { needsReview = true; error.needsReview = true; throw error; }
      },
      async planPcQuantityUpdates(cart, key, quantity) {
        assert(enabled && config.reconciliationEnabled === true && !needsReview && !reconciling, 'Personalization cart review is unavailable.');
        const item = cart.items?.find(x => x.key === key), id = item?.properties?._pc_quote;
        assert(typeof id === 'string' && /^[0-9a-f-]{36}$/.test(id), 'Personalized quote is unavailable.');
        const quote = await request(`${proxy}/quote/${encodeURIComponent(id)}`, {}, true);
        return pcQuantityPlan(cart, key, Number(quantity), quote, PRODUCTS, FEE_IDS);
      },
      async applyQuantityChange(cart, key, quantity, sections, sectionsUrl) {
        assert(enabled && config.reconciliationEnabled === true && !needsReview && !reconciling, 'Cart review is unavailable.');
        assert(Number.isSafeInteger(quantity) && quantity >= 0 && quantity <= 100, 'Invalid quantity.');
        reconciling = true;
        try {
        const inputs = await existingPersonalizedInputs(cart, true);
        const selected = cart.items.find(item => item.key === key);
        assert(selected && !selected.properties?._pc_component, 'Select a personalized product.');
        const parents = cart.items.filter(item => inputs.oldKeys.includes(item.key) && !item.properties?._pc_component);
        const index = parents.findIndex(item => item.key === key);
        assert(index >= 0, 'Product identity changed.');
        const bases = inputs.bases.map(x => ({...x})), requested = inputs.requested.map(x => ({...x}));
        if (quantity === 0) { bases.splice(index, 1); requested.splice(index, 1); }
        else { bases[index].quantity = quantity; requested[index].quantity = quantity; }
        const beforeHash = await cartHash(cart);
        if (!bases.length) {
          assert(await cartHash(await request(`${root}cart.js`)) === beforeHash, 'Your cart changed.');
          let started = false;
          try {
            started = true;
            const response = await request(`${root}cart/update.js`, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({updates:Object.fromEntries(inputs.oldKeys.map(k => [k,0])), sections, sections_url:sectionsUrl})});
            const after = await request(`${root}cart.js`);
            assert(cartState(after) === cartState({...cart, items:cart.items.filter(item => !inputs.oldKeys.includes(item.key))}), 'Your cart needs review.');
            return {...after, sections:response.sections};
          } catch (error) { if (started) needsReview = true; error.needsReview = needsReview; throw error; }
        }
        const quote = await request(`${proxy}/cart-quote`, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({lines:requested, spec:'Standard chest'})}, true);
        const payload = quoteToCartPayload(quote, bases, requested, {sections, sectionsUrl});
        return await this.applyReconciliation({before:cart, beforeHash, oldKeys:inputs.oldKeys, quote, payload}, lockToken);
        } finally { reconciling = false; }
      },
      async prepareReconciliation() {
        assert(enabled && config.reconciliationEnabled === true && !needsReview, 'Cart reconciliation is not enabled.');
        const before = await request(`${root}cart.js`);
        assert(before.currency === 'USD', 'A USD cart is required.');
        const inputs = await existingPersonalizedInputs(before);
        assert(inputs.bases.length > 0, 'No personalized product is available to review.');
        const quote = await request(`${proxy}/cart-quote`, {method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({lines: inputs.requested, spec: 'Standard chest'})}, true);
        return {before, beforeHash: await cartHash(before), oldKeys: inputs.oldKeys, quote,
          payload: quoteToCartPayload(quote, inputs.bases, inputs.requested)};
      },
      // No automatic checkout listener calls this. Candidate only, separately
      // gated. Ajax has no compare-and-swap: these checks do not make it atomic.
      async applyReconciliation(plan, token) {
        const locked = token === lockToken;
        assert(enabled && config.reconciliationEnabled === true && !needsReview && (!reconciling || locked), 'Cart reconciliation is not enabled.');
        reconciling = true;
        let mutationStarted = false;
        try {
          const current = await request(`${root}cart.js`);
          assert(current.currency === 'USD', 'A USD cart is required.');
          assert(await cartHash(current) === plan.beforeHash, 'Your cart changed. Review it again before checkout.');
          mutationStarted = true;
          // New quote markers ensure these additions cannot merge with old quote
          // lines. Old lines remain recoverable if add fails or times out.
          const parents = plan.payload.items.filter(item => !item.properties._pc_component);
          const fees = plan.payload.items.filter(item => item.properties._pc_component);
          let addResponse = await request(`${root}cart/add.js`, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({items:parents, ...(!fees.length && !plan.oldKeys.length && plan.payload.sections?.length ? {sections:plan.payload.sections, sections_url:plan.payload.sections_url} : {})})});
          const parentCart = await request(`${root}cart.js`);
          const previousParents = parentCart.items.filter(item => item.properties?._pc_quote !== plan.quote.quoteId);
          assert(cartState({...parentCart, items:previousParents}) === cartState(plan.before), 'Your cart changed during review.');
          const parentKeys = new Map();
          for (const line of plan.quote.lines) {
            const matches = parentCart.items.filter(item => item.properties?._pc_quote === plan.quote.quoteId && !item.properties?._pc_component && item.properties?._pc_line === line.lineRef);
            assert(matches.length === 1 && matches[0].key, 'Parent identity could not be verified.');
            parentKeys.set(line.lineRef, matches[0].key);
          }
          assert(parentCart.items.filter(item => item.properties?._pc_quote === plan.quote.quoteId).length === parents.length, 'Unexpected new cart lines.');
          const parentHash = await cartHash(parentCart);
          assert(await cartHash(await request(`${root}cart.js`)) === parentHash, 'Your cart changed during review.');
          if (fees.length) addResponse = await request(`${root}cart/add.js`, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({items:fees.map(fee => ({...fee, parent_line_key:parentKeys.get(feeOwner(plan.quote, fee))})), ...(!plan.oldKeys.length && plan.payload.sections?.length ? {sections:plan.payload.sections, sections_url:plan.payload.sections_url} : {})})});
          const added = await request(`${root}cart.js`);
          verifyCartReadback(added, plan);
          const previous = added.items.filter(item => item.properties?._pc_quote !== plan.quote.quoteId);
          assert(cartState({...added, items: previous}) === cartState(plan.before), 'Your cart changed during review. Please inspect it before checkout.');
          const addedHash = await cartHash(added);
          const currentAdded = await request(`${root}cart.js`);
          assert(currentAdded.currency === 'USD', 'A USD cart is required.');
          assert(await cartHash(currentAdded) === addedHash, 'Your cart changed during review. Please inspect it before checkout.');
          // Remove only the exact old _pc keys. Never clear the cart or touch an
          // ordinary/legacy line, and never delete by variant ID.
          const updates = Object.fromEntries(plan.oldKeys.map(key => [key, 0]));
          const updateResponse = plan.oldKeys.length ? await request(`${root}cart/update.js`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({updates, ...(plan.payload.sections?.length ? {sections:plan.payload.sections,sections_url:plan.payload.sections_url} : {})})}) : addResponse;
          const after = await request(`${root}cart.js`);
          verifyCartReadback(after, plan);
          const ordinaryBefore = plan.before.items.filter(item => !plan.oldKeys.includes(item.key));
          const ordinaryAfter = after.items.filter(item => item.properties?._pc_quote !== plan.quote.quoteId);
          assert(cartState({...after, items: ordinaryAfter}) === cartState({...plan.before, items: ordinaryBefore}), 'Your cart needs review before checkout.');
          if (plan.productAdd) {
            const selected = after.items.filter(item => item.properties?._pc_quote === plan.quote.quoteId && item.properties?._pc_line === plan.newLineRef && !item.properties?._pc_component);
            assert(selected.length === 1, 'New product could not be verified.');
            if (plan.payload.sections?.length) assert(updateResponse.sections && plan.payload.sections.every(id => typeof updateResponse.sections[id] === 'string'), 'Cart sections could not be refreshed.');
            return {...selected[0], sections:updateResponse.sections};
          }
          return {...after, sections:updateResponse.sections};
        } catch (error) {
          if (mutationStarted) needsReview = true;
          // An ambiguous write is never blindly retried or auto-rolled back.
          error.needsReview = needsReview;
          throw error;
        } finally { if (!locked) reconciling = false; }
      },
    });
  }

  const api = {ENABLED, PRODUCTS, FEE_IDS, GUEST_AUTH_KEY, productFormLine, selectionLine, quoteToCartPayload, verifyCartReadback,
    cartHash, reconciliationInputs, normalizeDetails, createAdapter};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') {
    let config = {enabled: ENABLED};
    try { config = {...config, ...JSON.parse(document.querySelector('[data-wcf-personalization-checkout-config]')?.textContent || '{}')}; } catch (_) {}
    window.wcfPersonalizationCheckout = createAdapter(config, window.fetch.bind(window), window.location.origin);
  }
})();
